import { useCallback, useState } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";

import { useAuth } from "@/contexts/AuthContext";
import { useKanbanApi, type KanbanAddOrigin } from "@/utils/kanban-api";
import type { KanbanCard, KanbanEtapa } from "@/types/kanban";

export interface FreelancerParaVaga {
  id: string;
  nome: string;
}

export interface EnvioParaVagaResultado {
  enviados: string[];
  jaExistentes: string[];
  falhas: { nome: string; motivo: string }[];
}

/**
 * Etapa de entrada da vaga: a primeira etapa "ativa" (menor ordem), que nos
 * templates padrão é a "Selecionados". Cai na primeira etapa qualquer se a vaga
 * tiver um template fora do padrão.
 */
export function getEtapaDeEntrada(etapas: KanbanEtapa[]): KanbanEtapa | undefined {
  const ordenadas = [...etapas].sort((a, b) => a.ordem - b.ordem);
  return ordenadas.find((etapa) => etapa.tipo === "ativa") ?? ordenadas[0];
}

/** Etapas que podem receber um candidato novo (nem final, nem drop). */
export function getEtapasDeDestino(etapas: KanbanEtapa[]): KanbanEtapa[] {
  return etapas.filter((etapa) => etapa.tipo !== "drop" && etapa.tipo !== "final");
}

export interface VagaSelection {
  /** Sem posição nesta vaga: entram na etapa de entrada. */
  newIds: string[];
  /** Descartados na própria etapa: voltam com a reativação. */
  droppedInPlace: KanbanCard[];
  /** Movidos para a etapa de reprovação: só voltam movendo o card no quadro. */
  reprovados: KanbanCard[];
}

/**
 * Separa os freelancers selecionados na lista pela situação no quadro da vaga.
 * Quem já é candidato ativo fica de fora: não há nada a fazer com ele.
 */
export function classifyVagaSelection(ids: string[], cards: KanbanCard[]): VagaSelection {
  const cardByFreelancer = new Map(cards.map((card) => [card.freelancer_id, card]));
  const selection: VagaSelection = { newIds: [], droppedInPlace: [], reprovados: [] };
  for (const id of ids) {
    const card = cardByFreelancer.get(id);
    if (!card) selection.newIds.push(id);
    else if (!card.is_dropped) continue;
    else if (card.drop_tipo === "in_place") selection.droppedInPlace.push(card);
    else selection.reprovados.push(card);
  }
  return selection;
}

/** Explica como trazer de volta quem foi movido para a etapa de reprovação. */
export function rejectedCandidatesNotice(reprovados: KanbanCard[]): string {
  if (reprovados.length === 0) return "";
  const nomes = reprovados.map((card) => card.freelancer?.nome ?? card.freelancer_id).join(", ");
  if (reprovados.length === 1) {
    const etapa = reprovados[0].etapa?.nome ?? "Reprovado";
    return `${nomes} está em "${etapa}": para trazer de volta, mova o card no quadro da vaga.`;
  }
  return `${nomes} estão na etapa de reprovação: para trazer de volta, mova os cards no quadro da vaga.`;
}

/**
 * Revalida o quadro e as métricas da vaga e os contadores de candidatos da
 * listagem de vagas (board e lista), depois de qualquer mudança de candidatos.
 */
export function invalidateVagaKanban(queryClient: QueryClient, vagaId: string) {
  for (const key of [
    "kanban-cards",
    "kanban-cards-all",
    "kanban-cards-drop-in-place",
    "kanban-metricas",
    "vaga",
  ]) {
    queryClient.invalidateQueries({ queryKey: [key, vagaId] });
  }
  for (const key of [
    "vagas-column",
    "vagas-counts",
    "vagas-list-page",
    "vagas-list-count",
  ]) {
    queryClient.invalidateQueries({ queryKey: [key] });
  }
}

/**
 * Vincula freelancers a uma vaga criando a posição no kanban dela (fonte única
 * de verdade dos candidatos). O insert no histórico feito pelo `addCandidate`
 * também alimenta a timeline do freelancer (evento `selecionado_para_vaga`).
 *
 * Envia um por um de propósito: é o que permite dizer exatamente quem entrou,
 * quem já era candidato e quem falhou, em vez de abortar o lote no primeiro erro.
 *
 * NÃO grava em `vagas.freelancers_selecionados`: o array é legado e não tem mais
 * nenhum leitor vivo (ver Propostas/deprecar-freelancers-selecionados-plano.md).
 */
export function useEnviarFreelancersParaVaga() {
  const kanbanApi = useKanbanApi();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [isEnviando, setIsEnviando] = useState(false);

  const enviar = useCallback(
    async (
      vagaId: string,
      etapaId: string,
      freelancers: FreelancerParaVaga[],
      origem: KanbanAddOrigin,
    ): Promise<EnvioParaVagaResultado> => {
      setIsEnviando(true);
      const resultado: EnvioParaVagaResultado = {
        enviados: [],
        jaExistentes: [],
        falhas: [],
      };

      try {
        // Ordem inicial = quantos já estão na etapa de destino, para o candidato
        // novo entrar no fim da coluna em vez de empatar em ordem 0 com os outros.
        let ordem = 0;
        try {
          const cards = await kanbanApi.getCards(vagaId, true);
          ordem = cards.filter((card) => card.etapa_atual_id === etapaId).length;
        } catch (err) {
          console.error("Falha ao ler os cards da vaga destino:", err);
        }

        for (const freelancer of freelancers) {
          try {
            await kanbanApi.addCandidate(vagaId, freelancer.id, etapaId, {
              origem,
              ordem,
              usuarioId: user?.id,
            });
            resultado.enviados.push(freelancer.nome);
            ordem += 1;
          } catch (err) {
            const erro = err as { code?: string; message?: string };
            // 23505 = já tem posição nesta vaga.
            if (erro?.code === "23505") {
              resultado.jaExistentes.push(freelancer.nome);
            } else {
              resultado.falhas.push({
                nome: freelancer.nome,
                motivo: erro?.message || "Erro desconhecido",
              });
              console.error("Erro ao vincular freelancer à vaga:", freelancer, err);
            }
          }
        }

        if (resultado.enviados.length > 0) {
          invalidateVagaKanban(queryClient, vagaId);
        }

        return resultado;
      } finally {
        setIsEnviando(false);
      }
    },
    [kanbanApi, queryClient, user?.id],
  );

  /** Reativa candidatos descartados na própria etapa, um por um. */
  const reactivate = useCallback(
    async (vagaId: string, cards: KanbanCard[]): Promise<Omit<EnvioParaVagaResultado, "jaExistentes">> => {
      const resultado: Omit<EnvioParaVagaResultado, "jaExistentes"> = { enviados: [], falhas: [] };
      for (const card of cards) {
        const nome = card.freelancer?.nome ?? card.freelancer_id;
        try {
          await kanbanApi.reactivateCardInPlace(card.id, "Reativado ao ser selecionado de novo na lista", user?.id);
          resultado.enviados.push(nome);
        } catch (err) {
          const erro = err as { message?: string };
          resultado.falhas.push({ nome, motivo: erro?.message || "Erro desconhecido" });
          console.error("Erro ao reativar candidato descartado:", card, err);
        }
      }
      if (resultado.enviados.length > 0) invalidateVagaKanban(queryClient, vagaId);
      return resultado;
    },
    [kanbanApi, queryClient, user?.id],
  );

  return { enviar, reactivate, isEnviando };
}
